import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Box, Flex, Badge } from "@chakra-ui/react";
import { Eye, Archive, Plus, EllipsisVertical, ShieldAlert, Edit } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";
import AddBankDetailsDrawer from "./components/AddBankDetailsDrawer";
import ViewBankDetailsDrawer from "./components/ViewBankDetailsDrawer";
import DeleteBankDetailsModal from "./components/DeleteBankDetailsModal";
import EditBankDetailsDrawer from "./components/EditBankDetailsDrawer";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchBankDetailsAction,
  createBankDetailsAction,
  deleteBankDetailsAction,
  updateBankDetailsAction,
} from "../../../state/actions/bankDetailActions";

import { useCustomNotification } from "components/Toasts/OpenNotification";
import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

const ManageBankDetailsTab = () => {
  const dispatch = useDispatch();
  const notify = useCustomNotification(); 
  const { bankDetails, loading } = useSelector((state) => state.bank);

  const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
  const [isViewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setEditDrawerOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);

  useEffect(() => {
    dispatch(fetchBankDetailsAction());
  }, [dispatch]);

  const openAddDrawer = () => setAddDrawerOpen(true);
  const closeAddDrawer = () => setAddDrawerOpen(false);

  const openViewDrawer = (bank) => {
    setSelectedBank(bank);
    setViewDrawerOpen(true);
  };

  const openEditDrawer = (bank) => {
    setSelectedBank(bank);
    setEditDrawerOpen(true);
  };

  const openDeleteModal = (bank) => {
    setSelectedBank(bank);
    setDeleteModalOpen(true);
  };

  
  const handleAddBank = async (data) => {
    const response = await dispatch(createBankDetailsAction(
      data.bank_name,
      data.branch_name,
      data.account_number,
      data.swift_code,
      data.branch_code
    ));

    if (response.success) {
      closeAddDrawer();
      notify({
        type: "success",
        message: "Bank added",
        description: "Bank details added successfully."
      });
    } else {
      notify({
        type: "error",
        message: "Error",
        description: response.error || "Failed to add bank details."
      });
    }
  };

  const handleUpdateBank = async (data) => {
    if (!selectedBank) return;
    const response = await dispatch(updateBankDetailsAction(selectedBank.bank_id, data));

    if (response.success) {
      setEditDrawerOpen(false);
      notify({
        type: "success",
        message: "Updated",
        description: "Bank details updated successfully."
      });
    } else {
      notify({
        type: "error",
        message: "Error",
        description: response.error || "Failed to update bank details."
      });
    }
  };

  const handleConfirmDelete = async () => {
    const response = await dispatch(deleteBankDetailsAction(selectedBank.bank_id));

    if (response.success) {
      setDeleteModalOpen(false);
      notify({
        type: "success",
        message: "Deleted",
        description: "Bank details deleted successfully."
      });
    } else {
      notify({
        type: "error",
        message: "Error",
        description: response.error || "Failed to delete bank details."
      });
    }
  };

  const generateMenuItems = (row) => [
    {
      icon: <Eye size={16} />,
      text: "View Details",
      onClick: () => openViewDrawer(row.original),
    },
    {
      icon: <Edit size={16} />,
      text: "Edit",
      onClick: () => openEditDrawer(row.original),
    },
    {
      icon: <Archive size={16} />,
      text: "Delete",
      onClick: () => openDeleteModal(row.original),
      extraIcon: <ShieldAlert size={16} color="pink" />,
    },
  ];

  const columnsData = [
    { Header: "Bank Name", accessor: "bank_name" },
    { Header: "Branch", accessor: "branch_name" },
    { Header: "Account Number", accessor: "account_number" },
   {
    Header: "Archived",
    accessor: "is_archived",
    Cell: ({ value }) => (
        <StatusBadge
            label={value ? "Yes" : "No"}
            variant={value ? "danger" : "success"}
        />
    ),
},
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ThreeDotMenu icon={<EllipsisVertical size={20} color="grey" />} items={generateMenuItems(row)} />
      ),
    },
  ];

  return (
    <Box>
      <Flex justifyContent="end" mb={4} pt={{ base: "120px", md: "50px" }}>
        {loading ? (
          <Skeleton width="140px" height="40px" borderRadius="md" />
        ) : (
          <GradientButton icon={<Plus size={16} />} height="40px" mr={5} onClick={openAddDrawer}>
            Add Bank Details
          </GradientButton>
        )}
      </Flex>

      <ReusableTable columnsData={columnsData} tableData={bankDetails || []} isLoading={loading} />

      <AddBankDetailsDrawer isOpen={isAddDrawerOpen} onClose={closeAddDrawer} onAddBank={handleAddBank} />

      <ViewBankDetailsDrawer isOpen={isViewDrawerOpen} onClose={() => setViewDrawerOpen(false)} bank={selectedBank} />

      <EditBankDetailsDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        bank={selectedBank}
        onUpdateBank={handleUpdateBank}
      />

      <DeleteBankDetailsModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        bank={selectedBank}
      />
    </Box>
  );
};

export default ManageBankDetailsTab;
